import type { Anchor, ChatMessage, ChatScope, ModelInfo, Note } from '../shared/types';
import { toast } from './toast';
import { connectionOf, modelStatus, modelSummary } from './models';
import { openDrawer } from './drawers';

export interface CompanionHooks {
  getNotes(): Note[];
  getHighlightCount(): number;
  addNote(text: string): Promise<void>;
  deleteNote(id: string): Promise<void>;
  jumpToAnchor(anchor: Anchor): void;
  getModels(): ModelInfo[];
  getModel(): string;
  setModel(id: string): void;
  getMessages(): ChatMessage[];
  getScope(): ChatScope;
  setScope(scope: ChatScope): void;
  sendChat(text: string): Promise<void>;
  askSelection(action: 'explain' | 'context'): void;
  refreshChat(): Promise<void>;
  /** 当前打开的书名与章节名（用于网页版预填文本），未开书为 null */
  getLocation(): { bookTitle: string; chapterTitle: string } | null;
}

let hooks: CompanionHooks;
/** 当前是否有选段（控制「选段」范围按钮的可用态） */
let hasQuote = false;
/** 当前选段原文（完整，未截断），用于网页版预填 */
let quoteText: string | null = null;

function el(id: string): HTMLElement {
  return document.getElementById(id)!;
}

// ---------- Tab 切换 ----------

function activateTab(tab: 'chat' | 'notes') {
  el('tabChat').classList.toggle('active', tab === 'chat');
  el('tabNotes').classList.toggle('active', tab === 'notes');
  el('paneChat').classList.toggle('active', tab === 'chat');
  el('paneNotes').classList.toggle('active', tab === 'notes');
}

export function focusNoteInput() {
  activateTab('notes');
  openDrawer('companion', el('noteInput'));
}

export function focusChatInput() {
  activateTab('chat');
  openDrawer('companion', el('chatInput'));
}

// ---------- 当前选段 ----------

export function setQuote(text: string | null) {
  hasQuote = !!text;
  quoteText = text;
  const box = el('quoteBox');
  box.innerHTML = '';
  if (text) {
    box.textContent = `“${text.length > 140 ? `${text.slice(0, 140)}…` : text}”`;
  } else {
    const span = document.createElement('span');
    span.className = 'quote-empty';
    span.textContent = '在正文中选中一段文字';
    box.append(span);
  }
  // 无选段时「选段」范围按钮禁用灰显，选择本身保持不变
  renderScopeButtons();
}

// ---------- 范围切换 ----------

export function renderScopeButtons() {
  const scope = hooks.getScope();
  for (const btn of document.querySelectorAll<HTMLButtonElement>('.scope-btn')) {
    const s = btn.dataset['scope'] as ChatScope;
    btn.classList.toggle('active', s === scope);
    if (s === 'selection') btn.disabled = !hasQuote;
  }
}

// ---------- 消息流 ----------

function messageNode(message: ChatMessage): HTMLElement {
  const item = document.createElement('div');
  item.className = `msg ${message.role === 'user' ? 'me' : 'ai'}`;
  if (message.role === 'assistant') {
    const who = document.createElement('span');
    who.className = 'who';
    who.textContent = '✦';
    item.append(who);
  }
  const body = document.createElement('div');
  body.className = 'm-body';
  if (message.anchor) {
    const quote = document.createElement('div');
    quote.className = 'm-quote';
    quote.textContent = message.anchor.exact;
    quote.title = '跳转到原文';
    quote.addEventListener('click', () => hooks.jumpToAnchor(message.anchor!));
    body.append(quote);
  }
  const p = document.createElement('p');
  p.textContent = message.text;
  body.append(p);
  item.append(body);
  return item;
}

export function renderMessages() {
  const list = el('chatList');
  const messages = hooks.getMessages();
  list.innerHTML = '';

  if (messages.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'chat-empty ui';
    const model = hooks.getModels().find((item) => item.id === hooks.getModel());
    empty.textContent = !model ? '添加实例后，共读对话会留在这里。' : model.archived ? '这个实例已归档，可在管理中恢复。' : '选中一段文字，开始一起读';
    list.append(empty);
    return;
  }

  for (const message of messages) list.append(messageNode(message));
  list.scrollTop = list.scrollHeight;
}

/** 乐观渲染本地 user 气泡（流式期间使用，done 后由 renderMessages 以持久化数据重绘） */
export function appendLocalUserMessage(text: string, anchor: Anchor | null) {
  const list = el('chatList');
  list.querySelector('.chat-empty')?.remove();
  list.append(
    messageNode({ id: '', role: 'user', text, anchor, scope: 'selection', createdAt: Date.now() }),
  );
  list.scrollTop = list.scrollHeight;
}

/** 追加一个流式 assistant 气泡，返回增量写入控制器 */
export function beginAssistantBubble(): { append(delta: string): void; finish(): void; fail(message: string): void } {
  const list = el('chatList');
  list.querySelector('.chat-empty')?.remove();
  const item = document.createElement('div');
  item.className = 'msg ai streaming';
  const who = document.createElement('span');
  who.className = 'who';
  who.textContent = '✦';
  const body = document.createElement('div');
  body.className = 'm-body';
  const p = document.createElement('p');
  body.append(p);
  item.append(who, body);
  list.append(item);
  list.scrollTop = list.scrollHeight;
  return {
    append(delta) {
      p.textContent += delta;
      list.scrollTop = list.scrollHeight;
    },
    finish() {
      item.classList.remove('streaming');
    },
    fail(message) {
      item.classList.remove('streaming');
      item.classList.add('failed');
      p.textContent = message;
    },
  };
}

/** 未配置 provider 时的本地占位回复（不持久化） */
export function appendAssistantPending() {
  const list = el('chatList');
  list.querySelector('.chat-empty')?.remove();
  const item = document.createElement('div');
  item.className = 'msg ai pending';
  const who = document.createElement('span');
  who.className = 'who';
  who.textContent = '✦';
  const body = document.createElement('div');
  body.className = 'm-body';
  const p = document.createElement('p');
  const model = hooks.getModels().find((item) => item.id === hooks.getModel());
  p.textContent = model && connectionOf(model) === 'web'
    ? '问题已保存。可在模型网页版继续，网页回复不会自动同步回来。'
    : '问题已保存。外部 MCP 客户端写回后，点击“刷新回复”查看。';
  body.append(p);
  item.append(who, body);
  list.append(item);
  list.scrollTop = list.scrollHeight;
}

// ---------- 模型选择 ----------

export function renderModelSelect() {
  const select = el('modelSelect') as HTMLSelectElement;
  select.innerHTML = '';
  const models = hooks.getModels();
  for (const archived of [false, true]) {
    const group = document.createElement('optgroup');
    group.label = archived ? '已归档 · 查看历史' : '共读实例';
    for (const model of models.filter((item) => !!item.archived === archived)) {
      const option = document.createElement('option');
      option.value = model.id;
      option.textContent = [model.name, model.modelLabel].filter(Boolean).join(' · ');
      group.append(option);
    }
    if (group.children.length) select.append(group);
  }
  if (!models.length) {
    const option = document.createElement('option');
    option.textContent = '添加共读实例';
    option.value = '';
    select.append(option);
  }
  select.value = hooks.getModel();
  select.disabled = !models.length;
  renderInstanceState();
  renderWebContinue();
}

function renderInstanceState() {
  const model = hooks.getModels().find((item) => item.id === hooks.getModel());
  el('aiStatus').textContent = modelStatus(model);
  el('instanceWelcome').hidden = hooks.getModels().some((item) => !item.archived);
  el('instanceContext').textContent = model
    ? model.archived ? '已归档，仍可查看此前的共读。' : modelSummary(model) || modelStatus(model)
    : '';
  const cannotSend = !model || !!model.archived || (connectionOf(model) === 'api' && !model.hasProvider);
  for (const id of ['chatSend', 'askBtn', 'contextBtn']) (el(id) as HTMLButtonElement).disabled = cannotSend;
  const input = el('chatInput') as HTMLTextAreaElement;
  input.disabled = cannotSend;
  input.placeholder = !model ? '先添加一个共读实例' : model.archived ? '恢复实例后可以继续对话' : cannotSend ? '在“管理”中完成 API 配置' : '就这一段，随便聊…';
  el('refreshChat').hidden = !model || connectionOf(model) === 'api';
}

// ---------- 网页版继续 ----------

function currentWebModel(): ModelInfo | null {
  const model = hooks.getModels().find((m) => m.id === hooks.getModel());
  return model?.webUrl && !model.archived ? model : null;
}

export function renderWebContinue() {
  const btn = el('webContinue') as HTMLButtonElement;
  const model = currentWebModel();
  btn.hidden = !model;
  if (model) btn.textContent = '↗ 在模型网页版继续';
}

function openWebContinue() {
  const model = currentWebModel();
  if (!model?.webUrl) return;
  const input = el('chatInput') as HTMLTextAreaElement;
  const question = input.value.trim() || '就这段聊聊';
  const parts: string[] = [];
  if (quoteText) {
    const loc = hooks.getLocation();
    if (loc) parts.push(`《${loc.bookTitle}》 · ${loc.chapterTitle}`);
    parts.push(`> ${quoteText}`);
  }
  parts.push(question);
  // 点击后保留输入框内容
  try {
    const url = new URL(model.webUrl.replace('{q}', encodeURIComponent(parts.join('\n'))));
    if (url.protocol !== 'https:' && url.protocol !== 'http:') throw new Error('unsafe URL');
    window.open(url.href, '_blank', 'noopener,noreferrer');
  } catch {
    toast('模型网页版地址无效，请使用 HTTP 或 HTTPS 地址');
  }
}

// ---------- 随记 ----------

function formatTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 作者身份显示名：'me' → 我，模型 id → 模型名 */
function authorName(author: string): string {
  if (author === 'me') return '我';
  return hooks.getModels().find((m) => m.id === author)?.name ?? author;
}

export function renderNotes() {
  const list = el('noteList');
  const notes = hooks.getNotes();
  list.innerHTML = '';

  if (notes.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'note-list-empty ui';
    empty.textContent = '还没有批注';
    list.append(empty);
  }

  for (const note of notes) {
    const item = document.createElement('div');
    item.className = 'note-item';

    if (note.anchor) {
      const quote = document.createElement('div');
      quote.className = 'n-quote';
      quote.textContent = note.anchor.exact;
      quote.title = '跳转到原文';
      quote.addEventListener('click', () => hooks.jumpToAnchor(note.anchor!));
      item.append(quote);
    }

    const text = document.createElement('div');
    text.className = 'n-text';
    text.textContent = note.text;

    const time = document.createElement('div');
    time.className = 'n-time ui';
    const badge = document.createElement('span');
    badge.className = `n-badge ${note.author === 'me' ? 'human' : 'ai'}`;
    badge.textContent = note.author === 'me' ? '●' : '✦';
    time.append(badge, document.createTextNode(`${authorName(note.author)} · ${formatTime(note.updatedAt)}`));

    const del = document.createElement('button');
    del.className = 'n-del ui';
    del.textContent = '✕';
    del.title = '删除批注';
    del.addEventListener('click', () => void hooks.deleteNote(note.id));

    item.append(text, time, del);
    list.append(item);
  }

  el('noteCount').textContent = `${notes.length} 条批注 · ${hooks.getHighlightCount()} 条划线`;
}

// ---------- 初始化 ----------

export function initCompanion(h: CompanionHooks) {
  hooks = h;

  el('tabChat').addEventListener('click', () => activateTab('chat'));
  el('tabNotes').addEventListener('click', () => activateTab('notes'));

  // 聊天输入：Enter 发送 / Shift+Enter 换行
  const input = el('chatInput') as HTMLTextAreaElement;
  const sendFromInput = () => {
    const text = input.value.trim();
    if (!text) return;
    input.value = '';
    input.style.height = '';
    void hooks.sendChat(text);
  };
  el('chatSend').addEventListener('click', sendFromInput);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendFromInput();
    }
  });
  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
  });

  for (const btn of document.querySelectorAll<HTMLButtonElement>('.scope-btn')) {
    btn.addEventListener('click', () => {
      hooks.setScope(btn.dataset['scope'] as ChatScope);
      renderScopeButtons();
    });
  }

  (el('modelSelect') as HTMLSelectElement).addEventListener('change', (e) => {
    hooks.setModel((e.target as HTMLSelectElement).value);
    renderInstanceState();
    renderWebContinue();
  });

  el('webContinue').addEventListener('click', openWebContinue);
  el('refreshChat').addEventListener('click', () => void hooks.refreshChat());

  // 快捷提问
  el('askBtn').addEventListener('click', () => hooks.askSelection('explain'));
  el('contextBtn').addEventListener('click', () => hooks.askSelection('context'));

  // 随记
  el('noteSave').addEventListener('click', () => {
    void (async () => {
      const noteInput = el('noteInput') as HTMLTextAreaElement;
      const text = noteInput.value.trim();
      if (!text) {
        toast('先写点什么再保存');
        return;
      }
      await hooks.addNote(text);
      noteInput.value = '';
    })();
  });

  (el('noteInput') as HTMLTextAreaElement).addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      el('noteSave').click();
    }
  });

  setQuote(null);
  renderMessages();
  renderNotes();
}
